import { WornSkins } from "../render/skins/wornSkins";
import { SKIN_LOOKS } from "../render/skins/looks";
import { uiUrl } from "./portraits";
import "./styles/titleFrame.css";

/**
 * Title frames (MP-23, the Trophy Hall's `frame` cosmetics; v2 2026-10-21: ornate and alive — every tier crowned, a light
 * sweeping its metal, its gems twinkling, the gold laurel swaying): a painted frame round the Commander's name and title
 * wherever they are shown — the Commander sheet's head, the share cards (titleFrameCanvas.ts) and the Skins picker's
 * preview. The art is a 9-slice per metal (art/interface/frames/frame_<tier>@2x/@3x.png, tools/art/kit/gen_frames.py)
 * plus the gold frame's crest; the CSS reads each as a custom property (`--tf-<tier>`, set here like ui/kitArt.ts so a
 * single-file build's inlined data URLs work) and draws it on a `.tf-art` layer over the element, so the frame never
 * changes the layout. Looks only: nothing here reaches the sim.
 */
export type FrameTier = "bronze" | "silver" | "gold";
export const FRAME_TIERS: readonly FrameTier[] = ["bronze", "silver", "gold"];

/** The art's corner slice and edge tile in CSS px (gen_frames.py C, E): the CSS slices at C / (2C + E) = 40 %. */
export const FRAME_CORNER_PX = 24;
export const FRAME_EDGE_PX = 12;

/** A frame piece's URL at a density, absolute (a relative url() in a custom property resolves against the stylesheet). */
export function frameArtUrl(file: string, base = typeof document === "undefined" ? "http://localhost/" : document.baseURI): string {
  const u = uiUrl(`frames/${file}`);
  return u.startsWith("data:") ? u : new URL(u, base).href;
}

/**
 * The custom properties: `--tf-<tier>` the 9-slice; the crests (v2: every tier crowned) `--tf-crest` (the gold crest
 * whole, for the share cards), `--tf-crown` and `--tf-laurel-l` / `--tf-laurel-r` (its parts: the laurel sways under
 * the crown), `--tf-medal` (silver) and `--tf-plaque` (bronze); each @2x and @3x.
 */
export function frameArtVars(base?: string): Record<string, string> {
  const set = (name: string) => `-webkit-image-set(url("${frameArtUrl(`${name}@2x.png`, base)}") 2x, url("${frameArtUrl(`${name}@3x.png`, base)}") 3x)`;
  const out: Record<string, string> = {};
  for (const t of FRAME_TIERS) out[`--tf-${t}`] = set(`frame_${t}`);
  out["--tf-crest"] = set("crest_gold");
  out["--tf-crown"] = set("crown_gold");
  out["--tf-laurel-l"] = set("laurel_l");
  out["--tf-laurel-r"] = set("laurel_r");
  out["--tf-medal"] = set("crest_silver");
  out["--tf-plaque"] = set("crest_bronze");
  return out;
}

let installed = false;
function install(): void {
  if (installed || typeof document === "undefined") return;
  installed = true;
  for (const [k, v] of Object.entries(frameArtVars())) document.documentElement.style.setProperty(k, v);
}

/** The worn frame's metal, or null (none worn: the stock plain plate). */
export function wornFrameTier(): FrameTier | null {
  const id = WornSkins.get().frame ?? null;
  return id ? SKIN_LOOKS[id]?.frame ?? null : null;
}

/** Glints on the corner gems (silver, gold) or star studs (bronze), one per corner. */
const GEMS = `<i class="tf-gem g1"></i><i class="tf-gem g2"></i><i class="tf-gem g3"></i><i class="tf-gem g4"></i>`;
/** Each tier's crest on the top edge: the gold crown over two swaying laurel sprigs, the silver medallion, the bronze plaque. */
const CRESTS: Readonly<Record<FrameTier, string>> = {
  gold: `<i class="tf-laurel l"></i><i class="tf-laurel r"></i><i class="tf-crown"></i><i class="tf-glint"></i>`,
  silver: `<i class="tf-medal"></i><i class="tf-glint"></i>`,
  bronze: `<i class="tf-plaque"></i><i class="tf-glint"></i>`,
};

/**
 * The frame layers inside a framed element (cosmetics v2): the 9-slice; the effects layer over it — a light sweeping
 * across the metal (masked to the frame's own art) and the corner glints; and the tier's crest. The motion is CSS
 * transforms and opacity only, and none under reduced motion (titleFrame.css).
 */
export function frameLayersHtml(tier: FrameTier): string {
  return `<i class="tf-art" aria-hidden="true"></i><i class="tf-fx" aria-hidden="true"><i class="tf-sheen"><i></i></i>${GEMS}</i>`
    + `<i class="tf-crest" aria-hidden="true">${CRESTS[tier]}</i>`;
}

/**
 * Frames `el` (a name plate: the Commander sheet's head titles, the picker's preview plate) in `tier`, or takes a frame
 * off with null. Idempotent: a re-render calls it again.
 */
export function frameElement(el: HTMLElement | null, tier: FrameTier | null = wornFrameTier()): void {
  if (!el) return;
  install();
  el.querySelectorAll(":scope > .tf-art, :scope > .tf-fx, :scope > .tf-crest").forEach((n) => n.remove());
  el.classList.remove("tframe", ...FRAME_TIERS.map((t) => `tf-${t}`));
  if (!tier) return;
  el.classList.add("tframe", `tf-${tier}`);
  el.insertAdjacentHTML("beforeend", frameLayersHtml(tier));
}
